import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { useNavigate } from "react-router-dom";
import { Download, Wine } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { Seo } from "./Seo";
import { BRAND } from "./data";
import { useContent } from "./ContentContext";
import { useTx, txtStyle } from "./siteText";
import { useLightbox } from "./Lightbox";

const EASE = [0.16, 1, 0.3, 1];

const DishCard = ({ item, i, resolveImg, onOpen }) => (
  <motion.article
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: EASE, delay: (i % 3) * 0.06 }}
    className="dish-card bg-cantina-surface rounded-2xl border border-cantina-line overflow-hidden shadow-[0_6px_18px_rgba(23,35,59,0.05)] flex flex-col"
    data-testid={`fullmenu-item-${i}`}
  >
    {item.img && (
      <button onClick={onOpen} className="frame-img aspect-[4/3] bg-cantina-alt cursor-zoom-in block text-left" data-testid={`fullmenu-img-${i}`} aria-label={`View ${item.name}`}>
        <img src={resolveImg(item.img)} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
      </button>
    )}
    <div className="p-5 flex flex-col flex-1">
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <h3 className="font-display text-lg font-bold text-cantina-ink leading-tight">{item.name}</h3>
        <span className="font-sans text-base font-bold text-cantina-chili whitespace-nowrap">{item.price}</span>
      </div>
      {item.desc && <p className="font-sans text-sm leading-relaxed text-cantina-ink/60">{item.desc}</p>}
    </div>
  </motion.article>
);

export default function FullMenu() {
  const lenis = useLenis();
  const navigate = useNavigate();
  const { menu, settings, resolveImg } = useContent();
  const tx = useTx();
  const { show } = useLightbox();
  const [active, setActive] = useState(menu[0]?.id);
  const barRef = useRef(null);

  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [lenis]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    menu.forEach((c) => { const el = document.getElementById(c.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [menu]);

  useEffect(() => {
    const chip = barRef.current?.querySelector(`[data-chip="${active}"]`);
    chip?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active]);

  const go = (id) => {
    if (lenis) lenis.scrollTo(`#${id}`, { offset: -160, duration: 1.2 });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative bg-cantina-cream min-h-screen" data-testid="fullmenu-page">
      <Seo page="menu" path="/menu" />
      <Navbar />

      <header className="relative pt-[84px] bg-cantina-navy text-cantina-cream overflow-hidden">
        <div className="absolute inset-0 tile-watermark opacity-[0.06]" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20 text-center">
          <p className="font-script text-cantina-marigold mb-1" style={txtStyle(tx("menupage.eyebrow"))}>{tx("menupage.eyebrow").text}</p>
          <h1 className="font-display tracking-tight" style={txtStyle(tx("menupage.headline"))}>{tx("menupage.headline").text}</h1>
          <p className="mt-4 font-sans text-cantina-cream/70 max-w-xl mx-auto" style={txtStyle(tx("menupage.subtitle"))}>{tx("menupage.subtitle").text}</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <a href={BRAND.orderUrl} target="_blank" rel="noopener noreferrer" className="btn-primary inline-block bg-cantina-marigold text-cantina-navy font-sans tracking-wide px-8 py-4 rounded-full" style={{ boxShadow: "0 12px 30px rgba(233,196,106,0.35)", ...txtStyle(tx("menupage.orderLabel")) }} data-testid="fullmenu-header-order">
              {tx("menupage.orderLabel").text}
            </a>
            <button onClick={() => navigate("/drinks")} className="btn-outline inline-flex items-center gap-2 font-sans tracking-wide text-cantina-cream border-2 border-cantina-cream/40 hover:border-cantina-cream px-7 py-[14px] rounded-full" style={txtStyle(tx("menupage.barMenuLabel"))} data-testid="fullmenu-barmenu">
              <Wine size={16} /> {tx("menupage.barMenuLabel").text}
            </button>
            {settings.menus?.foodPdf && (
              <a href={resolveImg(settings.menus.foodPdf)} target="_blank" rel="noopener noreferrer" download className="btn-outline inline-flex items-center gap-2 font-sans tracking-wide text-cantina-cream border-2 border-cantina-cream/40 hover:border-cantina-cream px-7 py-[14px] rounded-full" style={txtStyle(tx("menupage.downloadLabel"))} data-testid="fullmenu-download">
                <Download size={16} /> {tx("menupage.downloadLabel").text}
              </a>
            )}
          </div>
        </div>
      </header>

      <div className="sticky top-[84px] z-40 bg-cantina-cream/92 backdrop-blur-xl border-b border-cantina-line">
        <div ref={barRef} className="max-w-[1400px] mx-auto px-4 md:px-10 flex gap-2 overflow-x-auto no-scrollbar py-3">
          {menu.map((c) => (
            <button key={c.id} data-chip={c.id} onClick={() => go(c.id)}
              className="shrink-0 font-sans text-sm font-semibold px-4 py-2 rounded-full transition-colors whitespace-nowrap"
              style={active === c.id ? { backgroundColor: "#0047AB", color: "#FDFBF7" } : { backgroundColor: "transparent", color: "#17233B" }}
              data-testid={`fullmenu-cat-${c.id}`}>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20">
        {menu.map((cat) => {
          const gallery = cat.items.filter((x) => x.img);
          return (
          <section key={cat.id} id={cat.id} className="scroll-mt-[160px] mb-20" data-testid={`fullmenu-section-${cat.id}`}>
            <div className="flex items-center gap-5 mb-8">
              <h2 className="font-display text-cantina-ink whitespace-nowrap" style={txtStyle(tx("menupage.categoryTitle"))}>{cat.name}</h2>
              <span className="dash-rule flex-1" />
            </div>
            <div className={cat.id === "sides"
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2"
              : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"}>
              {cat.items.map((item, i) =>
                cat.id === "sides" ? (
                  <div key={i} className="flex items-baseline justify-between gap-3 border-b border-cantina-line py-3" data-testid={`fullmenu-side-${i}`}>
                    <div>
                      <span className="font-display text-lg font-semibold text-cantina-ink">{item.name}</span>
                      {item.desc && <span className="font-sans text-sm text-cantina-ink/50 ml-2">{item.desc}</span>}
                    </div>
                    <span className="font-sans text-base font-bold text-cantina-chili whitespace-nowrap">{item.price}</span>
                  </div>
                ) : (
                  <DishCard key={i} item={item} i={i} resolveImg={resolveImg} onOpen={() => show(gallery, gallery.indexOf(item))} />
                )
              )}
            </div>
          </section>
          );
        })}

        <div className="flex justify-center">
          <a href={BRAND.orderUrl} target="_blank" rel="noopener noreferrer" className="btn-primary bg-cantina-blue text-cantina-cream font-sans tracking-wide px-10 py-4 rounded-full" style={txtStyle(tx("menupage.footerOrderLabel"))} data-testid="fullmenu-footer-order">
            {tx("menupage.footerOrderLabel").text}
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
}
